import { HttpBackend, HttpClient } from '@angular/common/http';
import { Provider } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  ChildTranslateServiceConfig,
  provideChildTranslateService,
  provideTranslateService,
  RootTranslateServiceConfig,
  TranslateLoader,
} from '@ngx-translate/core';
import {
  provideTranslateHttpLoader,
  TRANSLATE_HTTP_LOADER_CONFIG,
  TranslateHttpLoader,
  TranslateHttpLoaderConfig,
} from '@ngx-translate/http-loader';

declare const LOAD_UID: string;

const defaultLoaderConfig: TranslateHttpLoaderConfig = {
  prefix: './i18n/',
  suffix: `.json`,
  useHttpBackend: true,
  enforceLoading: false,
};

const getAppPath = (context: ContextService): string => {
  if (context.embedded) {
    return context.debug
      ? 'http://localhost:4200/i18n/'
      : `${context.website}/ClientScript/AngularElements/i18n/`;
  }
  return defaultLoaderConfig.prefix;
};

const getIntegrationPath = (context: ContextService): string => {
  return context.integration
    ? `${context.integration.url}/i18n/`
    : defaultLoaderConfig.prefix;
};

const getLoader = (
  factory: (context: ContextService) => TranslateHttpLoaderConfig,
): Provider[] => {
  return [
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useFactory: factory,
      deps: [ContextService],
    },
    {
      provide: TranslateLoader,
      useClass: TranslateHttpLoader,
      deps: [
        defaultLoaderConfig.useHttpBackend ? HttpBackend : HttpClient,
        TRANSLATE_HTTP_LOADER_CONFIG,
      ],
    },
  ];
};

export const applicationTranslateConfiguration: RootTranslateServiceConfig = {
  loader: getLoader((context) => ({
    ...defaultLoaderConfig,
    prefix: getAppPath(context),
    suffix: `.json?uid=${LOAD_UID}`,
  })),
  fallbackLang: 'en',
};

export const integrationTranslateConfiguration: RootTranslateServiceConfig = {
  loader: getLoader((context) => ({
    ...defaultLoaderConfig,
    prefix: getIntegrationPath(context),
    suffix: `.json?uid=${LOAD_UID}`,
  })),
  fallbackLang: 'en',
};

export const testingTranslateConfiguration: RootTranslateServiceConfig = {
  loader: provideTranslateHttpLoader(defaultLoaderConfig),
  fallbackLang: 'en',
};

export const provideTranslateServiceForApp = (): Provider[] =>
  provideTranslateService(applicationTranslateConfiguration);

export const provideTranslateServiceForIntegration = (): Provider[] =>
  provideTranslateService(integrationTranslateConfiguration);

export const provideTranslateServiceForTesting = (): Provider[] =>
  provideTranslateService(testingTranslateConfiguration);

export const provideTranslateServiceForChild = (
  childConfiguration: ChildTranslateServiceConfig,
): Provider[] => provideChildTranslateService(childConfiguration);
